import React, {useContext, useEffect, useState} from "react";
import {Box, Grid, IconButton, makeStyles, Typography} from "@material-ui/core";
import PropTypes from "prop-types";
import StreamlineIcon from "../../widget/streamline-icon/StreamlineIcon";
import clsx from "clsx";
import SubtractCircle
    from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/remove-add/subtract-circle.svg";
import AddCircle
    from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/remove-add/add-circle.svg";
import {useI18N, useLocation} from "../../../common/common";
import {CustomButton, ResponsiveDialog} from "@hapinest/theme";
import {ANNUAL_CHARGE_CURRENCY, DIRECT_BUY_MAX_SHARES} from "../../../common/constant";
import {CurrencyContext} from "../../../store/providers/CurrencyProvider";
import {useAnalytics} from "../../../store/providers/AnalyticsProvider";
import {useBreakpoints, useDirectSalesPrices} from "../../../hooks";
import PriceCostList from "../../widget/priceCost/PriceCostList";
import {withSnackbar} from "../../hoc/SnackbarHOC";

const useStyles = makeStyles((theme) => ({
    sharesContainer: {
        justifyContent: "center",
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3)
    },
    removeAddIcon: {
        fontSize: theme.spacing(3.5),
        color: theme.palette.primary.main,
        "&.disabled > *": {
            color: theme.palette.grey["200"],
        }
    },
    costContainer: {
        backgroundColor: theme.palette.secondary[50],
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2)
    },
    totalPanel: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2)
    },
    selectionHeader: {
        marginBottom: theme.spacing(2)
    },
    totalNote: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    noteText: {
        color: "rgba(0, 0, 0, 0.64)"
    },
    divider: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    continueButton: {
        float: "right"
    },
    actionsContainer: {
        width: "100%",
        gap: `${theme.spacing(2)}px`,
        display: "flex"
    }
}));

const AB_TEST_DATA = {
    abtest_id: "GN_isDirectSalesDisabled_20240809",
    abtest_name: "Show new direct sales flow",
    abtest_variation_id: "variant2",
    abtest_variation_name: "Variant 2"
};

const SelectSharesDialog = ({
    isOpen,
    onClose,
    sharesCount,
    redirectUrl,
    showSnackbar,
    // eslint-disable-next-line no-unused-vars
    closeSnackbar,
    ...props
}) => {
    const classes = useStyles();
    const {i18n} = useI18N();
    const {currency} = useContext(CurrencyContext)?.[0];
    const {navigate} = useLocation();
    const {isExtraSmallScreen} = useBreakpoints();
    const {
        getPriceElements,
        priceElements,
        priceTotal,
        isInitializing,
        error,
        originalTotal,
        pointsPromotionElement,
        loyaltyDiscountElement
    } = useDirectSalesPrices();
    const { sendCustomEvent, eventNames } = useAnalytics();

    const [shares, setShares] = useState(sharesCount ?? 1);
    const decreaseShares = () => setShares(prev => prev - 1);
    const increaseShares = () => setShares(prev => prev + 1);

    const handleContinue = () => {
        sendCustomEvent({
            event_name: eventNames.DIRECT_SALES_CONTINUE_BTN,
            [eventNames.DIRECT_SALES_CONTINUE_BTN]: {
                shares_no: shares,
                share_price: priceTotal,
                currency: currency,
            }
        });

        if(redirectUrl) {
            return navigate(redirectUrl + "?shares=" + shares);
        }

        onClose(shares);
    };

    useEffect(() => {
        if (!isInitializing) {
            getPriceElements(shares);
        }
    }, [shares, isInitializing, currency]);

    useEffect(() => {
        if (isOpen && error) {
            showSnackbar({
                type: "warning",
                message: i18n("common.label.generic_error"),
            });
        }
    }, [error, isOpen]);

    const handleClose = () => {
        sendCustomEvent({
            event_name: eventNames.DIRECT_SALES_CANCEL_BTN
        });
        onClose();
    };

    useEffect(() => {
        if (isOpen && sendCustomEvent) {
            if (redirectUrl) {
                sendCustomEvent({
                    event_name: eventNames.DIRECT_SALES_PROMPT_VIEW,
                    ...AB_TEST_DATA
                });
            }
            sendCustomEvent({
                event_name: eventNames.DIRECT_SALES_NOSHARES_POPUP
            });
        }
    }, [isOpen, sendCustomEvent]);

    return (
        <>
            <ResponsiveDialog
                open={isOpen}
                handleClose={() => onClose()}
                content={(
                    <Box px={isExtraSmallScreen() ? 0 : 4}>
                        <Typography
                            variant={"h3"}
                            align={"center"}
                        >
                            {i18n("view.buy_a_share.directly_select_shares_dialog.title")}
                        </Typography>

                        <Grid
                            container
                            alignItems="center"
                            className={classes.sharesContainer}
                        >
                            <Grid item >
                                <IconButton
                                    color={"primary"}
                                    disabled={shares === 1}
                                    onClick={decreaseShares}
                                >
                                    <StreamlineIcon
                                        className={clsx(classes.removeAddIcon, {["disabled"]: shares === 1})}
                                    >
                                        <SubtractCircle/>
                                    </StreamlineIcon>
                                </IconButton>
                            </Grid>

                            <Grid item >
                                <Box
                                    px={1.5}
                                >
                                    <Typography>
                                        {shares}
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item >
                                <IconButton
                                    disabled={shares === DIRECT_BUY_MAX_SHARES}
                                    onClick={increaseShares}
                                >
                                    <StreamlineIcon
                                        className={clsx(classes.removeAddIcon, {["disabled"]: shares === DIRECT_BUY_MAX_SHARES})}
                                    >
                                        <AddCircle/>
                                    </StreamlineIcon>
                                </IconButton>
                            </Grid>
                        </Grid>

                        <Box>
                            <PriceCostList
                                items={priceElements}
                                currency={currency}
                                total={priceTotal}
                                totalDescription={
                                    currency !== ANNUAL_CHARGE_CURRENCY
                                        ? i18n("view.buy_a_share.directly_select_shares_dialog.selection_preview.total_cost_disclaimer")
                                        : undefined
                                }
                                originalTotal={originalTotal}
                                pointsPromotionElement={pointsPromotionElement}
                                loyaltyDiscountElement={loyaltyDiscountElement}
                            />
                        </Box>
                    </Box>
                )}
                actions={
                    <Box
                        className={classes.actionsContainer}
                        px={isExtraSmallScreen() ? 2 : 6}
                    >
                        <CustomButton
                            variant="outlined"
                            color="black"
                            fullWidth
                            disableElevation
                            onClick={handleClose}
                        >
                            {i18n("common.label.cancel")}
                        </CustomButton>
                        <CustomButton
                            variant={"contained"}
                            disableElevation
                            className={classes.continueButton}
                            onClick={handleContinue}
                            fullWidth
                            disabled={error}
                        >
                            {i18n("common.label.continue")}
                        </CustomButton>
                    </Box>
                }

                {...props}
            />
        </>
    );
};

SelectSharesDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    sharesCount: PropTypes.number,
    redirectUrl: PropTypes.string,
    showSnackbar: PropTypes.func,
    closeSnackbar: PropTypes.func
};


export default withSnackbar(SelectSharesDialog);
